import type { KioskBindingV1 } from "./kiosk-binding";

export type KioskRuntimePhase =
  | "booting"
  | "operational"
  | "recovering"
  | "out-of-service"
  | "permission-denied"
  | "staff-login"
  | "staff-resume-choice"
  | "staff-setup";

export type KioskRuntimeState = {
  phase: KioskRuntimePhase;
  binding: KioskBindingV1 | null;
  reason?: string;
};

export type KioskRuntimeAction =
  | { type: "BOOT_WITH_BINDING"; binding: KioskBindingV1 }
  | { type: "BOOT_SIGNED_OUT_WITH_BINDING"; binding: KioskBindingV1 }
  | { type: "BOOT_WITHOUT_BINDING" }
  | { type: "OPEN_STAFF_LOGIN" }
  | { type: "OPEN_STAFF_RESUME_CHOICE" }
  | { type: "OPEN_STAFF_SETUP" }
  | { type: "START_RECOVERY"; reason: string }
  | { type: "PERMISSION_DENIED"; reason: string }
  | { type: "GO_OUT_OF_SERVICE"; reason: string }
  | { type: "BECOME_OPERATIONAL"; binding: KioskBindingV1 };

export const initialKioskRuntimeState: KioskRuntimeState = {
  phase: "booting",
  binding: null,
};

/**
 * The customer-facing screen a heartbeat should report. Only the operational
 * phase renders the checkout, so any other phase reports nothing regardless of
 * the last screen recorded.
 *
 * Deriving this from the phase rather than reading the recorded screen directly
 * is what makes the report independent of effect ordering: the heartbeat fires
 * synchronously when the phase changes, before the effect that clears the last
 * screen has run, so trusting that value would report a checkout that is gone.
 */
export function reportedCustomerView(
  phase: KioskRuntimePhase,
  lastReportedView: string | null,
): string {
  if (phase !== "operational") return "none";
  return lastReportedView ?? "none";
}

export function kioskRuntimeReducer(
  state: KioskRuntimeState,
  action: KioskRuntimeAction,
): KioskRuntimeState {
  switch (action.type) {
    case "BOOT_WITH_BINDING":
      return { phase: "operational", binding: action.binding };
    case "BOOT_SIGNED_OUT_WITH_BINDING":
      return {
        phase: "out-of-service",
        binding: action.binding,
        reason: "signed_out",
      };
    case "BOOT_WITHOUT_BINDING":
      return { phase: "out-of-service", binding: null, reason: "unconfigured" };
    case "OPEN_STAFF_LOGIN":
      return { ...state, phase: "staff-login", reason: undefined };
    case "OPEN_STAFF_RESUME_CHOICE":
      return { ...state, phase: "staff-resume-choice", reason: undefined };
    case "OPEN_STAFF_SETUP":
      return { ...state, phase: "staff-setup", reason: undefined };
    case "START_RECOVERY":
      if (state.phase === "permission-denied") return state;
      return { ...state, phase: "recovering", reason: action.reason };
    case "PERMISSION_DENIED":
      return { ...state, phase: "permission-denied", reason: action.reason };
    case "GO_OUT_OF_SERVICE":
      return { ...state, phase: "out-of-service", reason: action.reason };
    case "BECOME_OPERATIONAL":
      return { phase: "operational", binding: action.binding };
    default:
      return state;
  }
}
